/**
 * POS Products Page
 *
 * Lists POS products imported from the point-of-sale system.
 * Supports deep linking via URL params and query strings.
 * Uses server-side pagination with our Pagination component.
 */

import { useState, useCallback } from 'react';
import { PageHeader } from '@/components/ui';
import { useDebounce, useUrlParams, useDrawerRoute, usePermission } from '@/hooks';
import { PosProductsFilters, PosProductsTable, PosProductsSyncBar, PosProductDrawer } from '../components';
import { AssociateRecipeModal } from '@/features/recipes/components';
import { useAddRecipePosProduct } from '@/features/recipes/hooks';
import type { SortField, SortOrder } from '../components/PosProductsTable';
import { usePosProducts, usePosProductCategories, usePosSyncStatus, usePosSyncStream } from '../hooks';

type ProductType = 'PRODUCT' | 'OPTION' | '';

const DEFAULT_LIMIT = 20;

export default function PosProductsPage() {
  // Permissions
  const canCreate = usePermission('pos-products.create');
  const canDelete = usePermission('pos-products.delete');

  // URL-synced drawer state
  const drawer = useDrawerRoute('/products/pos', { paramName: 'productId' });

  // Associate recipe modal state
  const [associateProductId, setAssociateProductId] = useState<string | null>(null);
  const addPosProduct = useAddRecipePosProduct();

  // URL-synced filters using reusable hook
  const { params, setParam, setParams, resetParams } = useUrlParams({
    search: { default: '' },
    type: { default: '' as ProductType },
    category: { default: '' },
    missingRecipe: { default: false, type: 'boolean' },
    page: { default: 1, type: 'number' },
    limit: { default: DEFAULT_LIMIT, type: 'number' },
    sortBy: { default: 'name' as SortField },
    sortOrder: { default: 'asc' as SortOrder },
  });

  // Debounce search to avoid excessive API calls
  const debouncedSearch = useDebounce(params.search, 300);

  // Data fetching
  const { data, isLoading, isPlaceholderData } = usePosProducts({
    page: params.page,
    limit: params.limit,
    search: debouncedSearch || undefined,
    type: params.type || undefined,
    category: params.category || undefined,
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    missingRecipeOnly: params.missingRecipe || undefined,
  });
  const { data: categories, isLoading: isCategoriesLoading } = usePosProductCategories();
  const { data: syncStatus, isLoading: isSyncStatusLoading } = usePosSyncStatus();
  const syncStream = usePosSyncStream();

  // Handlers
  const handleSearchChange = useCallback((value: string) => {
    setParams({ search: value, page: 1 });
  }, [setParams]);

  const handleTypeChange = useCallback((type: 'PRODUCT' | 'OPTION' | null) => {
    setParams({ type: type || '', page: 1 });
  }, [setParams]);

  const handleCategoryChange = useCallback((category: string | null) => {
    setParams({ category: category || '', page: 1 });
  }, [setParams]);

  // Handle sort from DevExpress - receives field and order directly
  const handleSort = useCallback((field: SortField, order: SortOrder) => {
    setParams({ sortBy: field, sortOrder: order, page: 1 });
  }, [setParams]);

  // Handle page change from Pagination component
  const handlePageChange = useCallback((page: number) => {
    setParam('page', page);
  }, [setParam]);

  // Handle limit change from Pagination component
  const handleLimitChange = useCallback((limit: number) => {
    setParams({ limit, page: 1 }); // Reset to page 1 when changing limit
  }, [setParams]);

  const handleMissingRecipeChange = useCallback((value: boolean) => {
    setParams({ missingRecipe: value, page: 1 });
  }, [setParams]);

  const handleReset = useCallback(() => {
    resetParams();
  }, [resetParams]);

  const handleRowClick = useCallback((productId: string) => {
    drawer.open(productId);
  }, [drawer]);

  const handleDrawerClose = useCallback((open: boolean) => {
    if (!open) drawer.close();
  }, [drawer]);

  const handleAssociateRecipe = useCallback((productId: string) => {
    setAssociateProductId(productId);
  }, []);

  const handleRecipeSelected = useCallback((recipeId: string) => {
    if (associateProductId) {
      addPosProduct.mutate(
        { recipeId, posProductId: associateProductId },
        { onSuccess: () => setAssociateProductId(null) },
      );
    }
  }, [associateProductId, addPosProduct]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Produits POS"
        subtitle="Produits importes depuis le systeme de caisse"
      />

      <PosProductsSyncBar
        syncStatus={syncStatus}
        isLoading={isSyncStatusLoading}
        onSync={syncStream.startSync}
        onCancel={syncStream.cancelSync}
        isSyncing={syncStream.isLoading}
        progress={syncStream.progress}
      />

      <PosProductsFilters
        search={params.search}
        onSearchChange={handleSearchChange}
        showMissingRecipesOnly={params.missingRecipe}
        onShowMissingRecipesChange={handleMissingRecipeChange}
        type={params.type || null}
        onTypeChange={handleTypeChange}
        category={params.category || null}
        onCategoryChange={handleCategoryChange}
        categories={categories ?? []}
        isCategoriesLoading={isCategoriesLoading}
        onReset={handleReset}
      />

      <PosProductsTable
        products={data?.items ?? []}
        pagination={data?.pagination ?? { page: 1, limit: params.limit, total: 0, totalPages: 0 }}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        sortBy={params.sortBy}
        sortOrder={params.sortOrder}
        onSort={handleSort}
        onRowClick={handleRowClick}
        onAssociateRecipe={canCreate ? handleAssociateRecipe : undefined}
        isLoading={isLoading || isPlaceholderData}
        hasFilters={!!(params.search || params.type || params.category || params.missingRecipe)}
        onSyncClick={syncStream.startSync}
      />

      {/* Drawer */}
      <PosProductDrawer
        productId={drawer.resourceId}
        open={drawer.isOpen}
        onOpenChange={handleDrawerClose}
        canEdit={canCreate}
        canDelete={canDelete}
      />

      {/* Associate Recipe Modal (from table icon click) */}
      {canCreate && (
        <AssociateRecipeModal
          isOpen={!!associateProductId}
          onClose={() => setAssociateProductId(null)}
          onSelectRecipe={handleRecipeSelected}
        />
      )}
    </div>
  );
}
